import React, { useState, useMemo } from 'react';
import { useAITTheme, type AITTheme } from '@this/shared/ui/theme';
import { Link } from '@this/components/shared/ui/navigations/link';
import TransitList from '@this/domain/transit/transit_list';
import FlightList from '@this/domain/flight/flight_list';
import type { FlightType } from '@this/domain/search_query';
import { FLIGHT_TYPES } from '@this/domain/search_query';
import type SearchResultItem from '@this/domain/search_result_item';
import type SelectStore from '@this/domain/select_store';
import type SelectRepository from '@this/domain/select_repository';
import type SearchQueryItem from '@this/domain/search_query_item';
import SelectTransportList from './transport_list/transport_list';
import FlightListComponent from './flight_list';
import PopupListSelector from '../popup_list_selector/PopupListSelector';
import PopupListMultiSelector from '../popup_list_multi_selector/popup_list_multi_selector';
import FlightCondition from '../flight_condition/FlightCondition';

type TransportListAreaContentProps = {
  type: 'transport' | 'flight';
  listWrapper: TransitList | FlightList;
  icon: React.ReactNode;
  subtype: number | undefined;
  resultItem: SearchResultItem;
  store: SelectStore;
  repository: SelectRepository;
  direction?: string;
  queryItem?: SearchQueryItem;
  transportSegment: number | undefined;
  handleTransportSegmentClick: (i: number) => () => void;
  openFlightCondition: () => void;
  closeFlightCondition: () => void;
  isOpenFlightCondition: boolean;
  handleFlightConditionSubmit: (
    filteredUpgradeSeat: boolean,
    carrierIds: string[],
    changeablePrices: string[],
    showConnectingAir: boolean
  ) => void;
};

const TransportListAreaContent: React.FC<TransportListAreaContentProps> = ({
  type,
  listWrapper,
  icon,
  subtype,
  resultItem,
  store,
  repository,
  direction,
  queryItem,
  transportSegment,
  handleTransportSegmentClick,
  openFlightCondition,
  closeFlightCondition,
  isOpenFlightCondition,
  handleFlightConditionSubmit
}) => {
  const { theme } = useAITTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);
  const [shownFightFilter, setShownFightFilter] = useState(false);

  const isDomesticTransport = type === 'transport' && listWrapper instanceof TransitList;
  const isInternationalFlight = type === 'flight' && listWrapper instanceof FlightList;

  return (
    <>
      <div style={styles.header}>
        <span>
          <div style={styles.icon}>{icon}</div>
          <div style={styles.title}>
            <span>すべての候補(</span>
            <span data-wovn-ignore>{listWrapper.length({ subtype: subtype || 0 })}</span>
            <span>件)</span>
          </div>
        </span>

        {isDomesticTransport && !listWrapper.package && (
          <>
            <div style={styles.tabButtons}>
              <button
                type="submit"
                className="select-list-area__header__button transport-list-area__tab-button"
                style={styles.tabButton(transportSegment === 0)}
                onClick={handleTransportSegmentClick(0)}
                data-active={transportSegment === 0}
              >
                飛行機
              </button>
              <button
                type="submit"
                className="select-list-area__header__button transport-list-area__tab-button"
                style={styles.tabButton(transportSegment === 1)}
                onClick={handleTransportSegmentClick(1)}
                data-active={transportSegment === 1}
              >
                鉄道
              </button>
            </div>
            {transportSegment === 0 && (
              <div style={styles.flightConditions}>
                <button type="button" style={styles.conditionLink} onClick={() => openFlightCondition()}>
                  <img style={styles.conditionIconImg} src="/images/filter_icon.png" alt="" />
                  絞り込み
                </button>
              </div>
            )}
          </>
        )}

        {isInternationalFlight && subtype !== undefined && (
          <div style={styles.foreignFlightConditions}>
            <Link onClick={() => setShownFightFilter(!shownFightFilter)}>
              {shownFightFilter ? '詳細絞り込みを非表示' : '詳細絞り込みを表示'}
            </Link>
          </div>
        )}

        {store.result.type === 'jrPackage' && type === 'transport' && (
          <span style={styles.small}>
            ※パッケージ商品の新幹線チケットは日時の変更が一切できません
            <br />
            通常の個別新幹線チケットとは異なる種類のチケットですのでご注意ください
          </span>
        )}
      </div>

      {isInternationalFlight && subtype !== undefined && shownFightFilter && (
        <>
          <div className="select-pc__popup-list-wrapper">
            <div style={styles.popupSubWrapper}>
              <span style={styles.popupListMiniTitle}>+航空会社</span>
              <PopupListMultiSelector
                items={(store.result.query.carrierList || store.result.flightList!.carrierList).map(carrier => {
                  return { label: carrier.name, value: carrier.id };
                })}
                headerLabel="航空会社"
                selectableAll
                selectedValues={(queryItem && queryItem.carrierIds) || []}
                onChange={(values: string[]) => {
                  if (queryItem) {
                    queryItem.setCarrierIds(values);
                  }
                  listWrapper.handleAirlinesChange(subtype, values);
                }}
                onSelectAll={() => {
                  if (queryItem) {
                    queryItem.setCarrierIds(['all']);
                  }
                  listWrapper.resetAirlines(subtype);
                }}
              />
            </div>
            {queryItem && queryItem.airports && (
              <>
                <div style={styles.popupSubWrapper}>
                  <span style={styles.popupListMiniTitle}>+出発空港</span>
                  <PopupListSelector
                    items={queryItem.airports.origin}
                    headerLabel="出発空港"
                    name="originCode"
                    currentValue={queryItem.originCode || queryItem.airports.origin[0].value}
                    onChange={(value: string) => {
                      queryItem.setOriginCode(value);
                      if (queryItem.airports && queryItem.airports.origin[0].value === value) {
                        listWrapper.resetOriginCode(subtype);
                      } else {
                        listWrapper.handleOriginCodeChange(subtype, value);
                      }
                    }}
                  />
                </div>
                <div style={styles.popupSubWrapper}>
                  <span style={styles.popupListMiniTitle}>+到着空港</span>
                  <PopupListSelector
                    items={queryItem.airports.destination}
                    headerLabel="到着空港"
                    name="destCode"
                    currentValue={queryItem.destCode || queryItem.airports.destination[0].value}
                    onChange={(value: string) => {
                      queryItem.setDestCode(value);
                      if (queryItem.airports && queryItem.airports.destination[0].value === value) {
                        listWrapper.resetDestCode(subtype);
                      } else {
                        listWrapper.handleDestCodeChange(subtype, value);
                      }
                    }}
                  />
                </div>
              </>
            )}
            <div style={styles.popupSubWrapper}>
              <span style={styles.popupListMiniTitle}>+シートクラス</span>
              <PopupListMultiSelector
                items={[
                  { label: 'エコノミークラス', value: 'M' },
                  { label: 'プレミアムエコノミー', value: 'W' },
                  { label: 'ビジネスクラス', value: 'C' },
                  { label: 'ファーストクラス', value: 'F' }
                ]}
                headerLabel="シートクラス"
                selectableAll
                selectedValues={store.result.query.cabin}
                defaultSeatClasses={store.result.query.defaultSeatClassesForFilter()}
                onChange={(values: string[]) => {
                  repository.updateCabin(values);
                  listWrapper.handleCabinsChange(values);
                }}
                onSelectAll={() => {
                  repository.updateCabin(['all']);
                }}
              />
            </div>
          </div>
          <div className="select-pc__popup-list-wrapper">
            <div style={styles.popupSubWrapper}>
              <span style={styles.popupListMiniTitle}>+乗り継ぎ条件</span>
              <PopupListSelector
                items={[
                  { label: 'すべて', value: '' },
                  { label: '乗り継ぎのみ', value: 'C' },
                  { label: '直行便のみ', value: 'N' }
                ]}
                headerLabel="乗り継ぎ条件"
                name="flightType"
                currentValue={store.result.query.flightType || ''}
                onChange={(key: string) => {
                  if (!FLIGHT_TYPES.some(f => f === key)) {
                    return;
                  }

                  const flightType = key as FlightType;
                  repository.updateFlightType(flightType);
                  listWrapper.handleFlightType(subtype, flightType);
                }}
              />
            </div>
            <div style={styles.popupSubWrapper}>
              <span style={styles.popupListMiniTitle}>+変更・キャンセル条件</span>
              <PopupListSelector
                items={[
                  { label: 'すべて', value: 'all' },
                  { label: '変更可のみ', value: 'changeable' },
                  { label: 'キャンセル可のみ', value: 'cancelable' },
                  { label: '変更＆キャンセル可のみ', value: 'both' }
                ]}
                headerLabel="変更・キャンセル条件"
                name="extraKey"
                currentValue={listWrapper.minirule}
                onChange={(key: string) => {
                  if (key === 'all' || key === 'changeable' || key === 'cancelable' || key === 'both') {
                    listWrapper.handleMiniruleChange(key);
                  }
                }}
              />
            </div>
          </div>
        </>
      )}

      {isInternationalFlight && (
        <div style={styles.sortTabs}>
          <button
            type="button"
            style={styles.sortTab(listWrapper.sortKey === 'orderedIndex')}
            className="transport-list-area__sort-tab"
            onClick={listWrapper.handleSortKeyChange('orderedIndex')}
          >
            おすすめ順
          </button>
          <button
            type="button"
            style={styles.sortTab(listWrapper.sortKey === 'price')}
            className="transport-list-area__sort-tab"
            onClick={listWrapper.handleSortKeyChange('price')}
          >
            料金が安い順
          </button>
          <button
            type="button"
            style={styles.sortTab(listWrapper.sortKey === 'departure')}
            className="transport-list-area__sort-tab"
            onClick={listWrapper.handleSortKeyChange('departure')}
          >
            出発時刻が早い順
          </button>
          <button
            type="button"
            style={styles.sortTab(listWrapper.sortKey === 'time')}
            className="transport-list-area__sort-tab"
            onClick={listWrapper.handleSortKeyChange('time')}
          >
            所要時間が短い順
          </button>
        </div>
      )}

      {isDomesticTransport && !listWrapper.package && transportSegment === 1 && (
        <div style={styles.vacancyLegend}>○・・・残席あり、△・・・残席わずか、×・・・残席なし(選択できません)</div>
      )}

      {isDomesticTransport && !listWrapper.package && (
        <FlightCondition
          queryItem={queryItem}
          listWrapper={listWrapper}
          showUpgradeSeatBox={store.result.query.firstTravelerDomesticAirSeatUpgrade()}
          open={isOpenFlightCondition}
          onSubmit={handleFlightConditionSubmit}
          onAbort={closeFlightCondition}
        />
      )}

      <div style={styles.body}>
        {isDomesticTransport && (
          <SelectTransportList
            transits={listWrapper}
            resultItem={resultItem}
            repository={repository}
            store={store}
            onChange={store.setTab('none')}
            direction={direction}
            loading={false}
          />
        )}

        {isInternationalFlight && subtype !== undefined && (
          <FlightListComponent
            flights={listWrapper}
            type={subtype}
            repository={repository}
            store={store}
            onChange={store.setTab('none')}
          />
        )}
      </div>

      <div style={styles.footer} />
    </>
  );
};

const getStyles = (theme: AITTheme) => ({
  header: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    justifyContent: 'space-between',
    marginBottom: 10
  },
  icon: {
    display: 'inline-block',
    textAlign: 'center' as const,
    width: 20,
    height: 20,
    background: theme.iconColor,
    borderRadius: 10,
    color: 'white',
    fontWeight: 'bold' as const,
    marginRight: 10
  },
  title: {
    display: 'inline-block',
    fontWeight: 'bold' as const,
    marginRight: 10
  },
  footer: {
    marginTop: 10
  },
  tabButtons: {
    display: 'flex',
    alignItems: 'center',
    width: 172,
    border: `1px solid ${theme.linkColor}`,
    borderRadius: theme.buttonRadius
  },
  tabButton: (active: boolean) => ({
    display: 'inline-block',
    padding: '5px 0',
    backgroundColor: active ? theme.linkColor : '#fff',
    color: active ? '#fff' : theme.linkColor,
    borderRadius: 0,
    width: '50%',
    outline: 'none',
    border: 'none',
    cursor: 'pointer',
    fontFamily: 'inherit'
  }),
  flightConditions: {
    display: 'flex',
    justifyContent: 'space-between',
    flexBasis: '100%',
    fontSize: 12,
    fontWeight: 'bold' as const,
    marginTop: 10
  },
  foreignFlightConditions: {
    fontSize: 12
  },
  conditionLink: {
    color: '#404040',
    cursor: 'pointer',
    border: 'none',
    background: 'none',
    padding: 0,
    fontFamily: 'inherit',
    fontSize: 'inherit'
  },
  conditionIconImg: {
    margin: 'auto 2px auto 0',
    verticalAlign: 'middle' as const
  },
  small: {
    fontSize: 10
  },
  sortTabs: {
    display: 'flex',
    justifyContent: 'space-between'
  },
  sortTab: (active: boolean) => ({
    width: 'calc((100% - 5px * 3) / 4)',
    cursor: 'pointer',
    marginBottom: 15,
    fontWeight: 'bold' as const,
    color: active ? theme.linkColor : theme.grayTextColor,
    textAlign: 'center' as const,
    border: 'none',
    borderBottom: `2px solid ${active ? theme.linkColor : theme.grayTextColor}`,
    background: 'none',
    padding: 0,
    paddingBottom: 3,
    fontFamily: 'inherit',
    fontSize: 9
  }),
  body: {
    display: 'flex',
    flexDirection: 'column' as const,
    maxHeight: '66vh',
    overflowY: 'scroll' as const,
    WebkitOverflowScrolling: 'touch' as const
  },
  popupListMiniTitle: {
    fontSize: 10,
    color: '#404040',
    marginBottom: 3
  },
  popupSubWrapper: {
    flexDirection: 'row' as const
  },
  vacancyLegend: {
    fontSize: 10,
    color: theme.textColor,
    padding: '2px',
    lineHeight: 1.6
  }
});

export default TransportListAreaContent;
